<template>
<div :style="themePageStyle">
  <div class="container" style="padding-top:24px;padding-bottom:100px;max-width:720px;">

    <div class="d-flex align-items-center mb-3 no-print">
      <button class="btn btn-sm btn-outline-secondary me-2" @click="$router.push('/dashboard')">
        <i class="mdi mdi-arrow-left me-1"></i>Home
      </button>
      <img src="/assets/images/flockr_logo.png" alt="Flockr" style="height:28px;margin-right:8px;" />
      <span class="fw-semibold" :style="{color:darkMode?'#F5F0E8':'#1A1A1A'}">Bill</span>
      <div class="ms-auto">
        <button class="btn btn-sm btn-outline-warning" @click="printPdf">
          <i class="mdi mdi-download me-1"></i>Save PDF
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>

    <div v-else-if="!bill.id" class="text-center py-5">
      <i class="mdi mdi-alert-circle-outline" style="font-size:48px;color:#FF6B6B;"></i>
      <p class="mt-2">Bill not found or link expired.</p>
    </div>

    <div v-else>
      <!-- Bill card -->
      <div class="bv-card">
        <div class="d-flex justify-content-between align-items-start mb-3">
          <div>
            <div style="color:#6B6356;font-size:10px;text-transform:uppercase;letter-spacing:1px;font-weight:600;">Bill</div>
            <div style="color:#BFA164;font-weight:800;font-family:'Courier New',monospace;font-size:16px;">{{ bill.bill_number }}</div>
          </div>
          <span class="bv-status" :class="'bv-status-'+bill.status">{{ bill.status }}</span>
        </div>

        <!-- From (seller) -->
        <div class="bv-party-block">
          <div class="bv-party-label">From</div>
          <div class="d-flex align-items-center gap-3">
            <div class="bv-avatar">
              <img v-if="bill.seller_avatar" :src="photoUrl(bill.seller_avatar)" alt="" />
              <span v-else>{{ (bill.seller_name || '?').charAt(0).toUpperCase() }}</span>
            </div>
            <div>
              <div style="font-weight:700;font-size:15px;">
                {{ bill.seller_name }}
                <i v-if="Number(bill.seller_verified)" class="mdi mdi-check-decagram" style="color:#50C878;font-size:14px;" title="Verified seller"></i>
              </div>
              <div style="font-size:11px;color:#6B6356;">
                <span v-if="bill.seller_rating"><i class="mdi mdi-star" style="color:#FFAB00;"></i> {{ Number(bill.seller_rating).toFixed(1) }}</span>
                <span v-else>New on Flockr</span>
              </div>
            </div>
          </div>
        </div>

        <!-- To (buyer) -->
        <div class="bv-party-block">
          <div class="bv-party-label">To</div>
          <div style="font-weight:700;font-size:14px;">{{ bill.buyer_name || 'You' }}</div>
          <div style="font-size:11px;color:#6B6356;">{{ bill.buyer_phone ? '+' + bill.buyer_phone : '' }}</div>
        </div>

        <!-- Line items -->
        <div class="bv-section">
          <div class="bv-section-title">Items</div>
          <table class="bv-table">
            <thead>
              <tr><th>Description</th><th class="text-end">Qty</th><th class="text-end">Unit</th><th class="text-end">Total</th></tr>
            </thead>
            <tbody>
              <tr v-for="l in lines" :key="l.id">
                <td>{{ l.description }}</td>
                <td class="text-end">{{ l.quantity }}</td>
                <td class="text-end">{{ bill.currency }} {{ fNum(l.unit_price) }}</td>
                <td class="text-end fw-bold">{{ bill.currency }} {{ fNum(l.subtotal) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Totals -->
        <div class="bv-totals">
          <div class="bv-total-line"><span>Subtotal</span><span>{{ bill.currency }} {{ fNum(bill.subtotal) }}</span></div>
          <div v-if="Number(bill.transport_fee) > 0" class="bv-total-line"><span>Transport</span><span>{{ bill.currency }} {{ fNum(bill.transport_fee) }}</span></div>
          <div v-if="Number(bill.discount) > 0" class="bv-total-line" style="color:#50C878;"><span>Discount</span><span>−{{ bill.currency }} {{ fNum(bill.discount) }}</span></div>
          <div class="bv-total-line bv-total-grand"><span>Total to pay</span><span>{{ bill.currency }} {{ fNum(bill.total) }}</span></div>
        </div>

        <!-- Notes -->
        <div v-if="bill.notes" class="bv-notes">
          <div style="font-size:11px;color:#6B6356;font-weight:600;text-transform:uppercase;letter-spacing:.4px;">Notes from seller</div>
          <div style="font-size:13px;margin-top:4px;">{{ bill.notes }}</div>
        </div>

        <!-- Escrow badge -->
        <div class="bv-escrow-badge">
          <i class="mdi mdi-shield-check me-1"></i>
          <span>Escrow Protected · Funds held until you confirm delivery</span>
        </div>
      </div>

      <!-- Action panel -->
      <div v-if="bill.status === 'sent' || bill.status === 'countered'" class="bv-card no-print">
        <div v-if="!signedIn" class="text-center">
          <i class="mdi mdi-account-lock" style="font-size:32px;color:#BFA164;"></i>
          <p class="mt-2 mb-2">Sign in to accept this bill and pay via M-Pesa.</p>
          <button class="btn btn-warning fw-bold" @click="goToLogin">
            <i class="mdi mdi-login me-1"></i>Sign in to pay {{ bill.currency }} {{ fNum(bill.total) }}
          </button>
        </div>

        <div v-else-if="stkStatus === ''">
          <div class="mb-2" style="font-size:11px;color:#6B6356;font-weight:600;text-transform:uppercase;letter-spacing:.4px;">M-Pesa phone</div>
          <div style="display:flex;gap:8px;">
            <div style="flex:1;position:relative;">
              <span style="position:absolute;left:12px;top:50%;transform:translateY(-50%);color:#8A827A;font-size:13px;">+254</span>
              <input v-model="payPhone" type="tel" maxlength="10" placeholder="7XX XXX XXX" class="bv-input" style="padding-left:52px;" />
            </div>
          </div>
          <div class="d-flex gap-2 mt-3 flex-wrap">
            <button class="btn btn-warning fw-bold flex-fill" @click="acceptAndPay" :disabled="submitting">
              <span v-if="submitting"><span class="spinner-border spinner-border-sm me-1"></span>Sending prompt…</span>
              <span v-else><i class="mdi mdi-cellphone-wireless me-1"></i>Accept & Pay {{ bill.currency }} {{ fNum(bill.total) }}</span>
            </button>
          </div>
          <div class="d-flex gap-2 mt-2 flex-wrap">
            <button class="btn btn-sm btn-outline-secondary flex-fill" @click="showCounter = !showCounter">
              <i class="mdi mdi-swap-horizontal me-1"></i>Counter offer
            </button>
            <button class="btn btn-sm btn-outline-danger flex-fill" @click="reject">
              <i class="mdi mdi-close me-1"></i>Reject bill
            </button>
          </div>

          <!-- Counter-offer panel -->
          <div v-if="showCounter" class="mt-3 p-3" style="background:rgba(206,147,216,.06);border:1px solid rgba(206,147,216,.25);border-radius:8px;">
            <div style="font-size:12px;font-weight:700;color:#CE93D8;margin-bottom:6px;"><i class="mdi mdi-swap-horizontal me-1"></i>Propose a different amount</div>
            <label class="bv-label">New total ({{ bill.currency }})</label>
            <input v-model.number="counterAmount" type="number" step="1" min="1" class="bv-input" :placeholder="fNum(bill.total)" />
            <label class="bv-label mt-2">Message to seller</label>
            <textarea v-model="counterMessage" class="bv-input" rows="2" placeholder="e.g. We can only do this today at KES X, would that work?"></textarea>
            <button class="btn btn-sm btn-outline-secondary mt-2" @click="sendCounter" :disabled="submitting || !counterAmount">
              <i class="mdi mdi-send me-1"></i>Send counter to seller
            </button>
          </div>
        </div>

        <div v-else-if="stkStatus === 'stk_sent'" class="text-center">
          <i class="mdi mdi-cellphone-message" style="font-size:32px;color:#FFAB00;"></i>
          <p class="mt-2 mb-1">STK prompt sent to +254{{ payPhone }}</p>
          <p style="font-size:12px;color:#6B6356;">Enter your M-Pesa PIN to complete payment.</p>
          <div class="mt-2"><i class="mdi mdi-refresh mdi-spin me-1"></i>Checking… ({{ stkCountdown }}s)</div>
        </div>

        <div v-else-if="stkStatus === 'completed'" class="text-center">
          <i class="mdi mdi-check-circle" style="font-size:48px;color:#50C878;"></i>
          <h5 style="color:#50C878;">Payment received!</h5>
          <p style="font-size:12px;color:#6B6356;">Funds are in escrow. Seller will arrange delivery.</p>
          <button class="btn btn-warning fw-bold mt-2" @click="$router.push('/orders')">
            <i class="mdi mdi-receipt me-1"></i>View my orders
          </button>
        </div>

        <div v-else-if="stkStatus === 'failed'" class="text-center">
          <i class="mdi mdi-close-circle" style="font-size:32px;color:#FF6B6B;"></i>
          <p style="color:#FF6B6B;font-weight:600;">Payment failed</p>
          <p style="font-size:12px;color:#6B6356;">{{ stkError }}</p>
          <button class="btn btn-outline-warning" @click="stkStatus = ''">Try again</button>
        </div>
      </div>

      <div v-else-if="bill.status === 'paid'" class="bv-card text-center">
        <i class="mdi mdi-check-circle" style="font-size:48px;color:#50C878;"></i>
        <h5 style="color:#50C878;">This bill is paid</h5>
        <p style="font-size:12px;color:#6B6356;">Order in progress. Track it in "My Orders".</p>
      </div>

      <div v-else class="bv-card text-center">
        <p style="color:#6B6356;">This bill is {{ bill.status }}.</p>
      </div>
    </div>

  </div>
</div>
</template>

<script>
/* eslint-disable */
import { execute, executeJson, flocker, uploads } from '@/api';
export default {
  name: 'BillView',
  data(){
    return {
      darkMode: (document.documentElement.getAttribute('data-theme') === 'dark'),
      loading: true,
      bill: {},
      lines: [],
      counters: [],
      payPhone: '',
      submitting: false,
      stkStatus: '',  // '' | stk_sent | completed | failed
      stkError: '',
      stkRef: '',
      stkCheckoutId: '',
      stkTimer: null,
      stkCountdown: 5,
      showCounter: false,
      counterAmount: null,
      counterMessage: ''
    };
  },
  computed: {
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    signedIn(){ return !!sessionStorage.getItem('userId'); }
  },
  mounted(){
    this.load();
    // Prefill phone from session if we know the user
    var uPhone = sessionStorage.getItem('userPhone') || '';
    if (uPhone) {
      var raw = uPhone.replace(/\D/g, '');
      if (raw.substr(0,3) === '254') raw = raw.substr(3);
      if (raw.charAt(0) === '0') raw = raw.substr(1);
      this.payPhone = raw;
    }
  },
  beforeUnmount(){ if (this.stkTimer) clearInterval(this.stkTimer); },
  methods: {
    fNum(n){ return Number(n || 0).toLocaleString('en-KE', { maximumFractionDigits: 2 }); },
    photoUrl(p){ if(!p) return ''; if(p.startsWith('http')) return p; return uploads + String(p).replace(/^uploads\//,''); },
    async load(){
      var token = this.$route.params.token;
      if (!token) { this.loading = false; return; }
      try {
        var r = await executeJson({ function: 'getBillByToken', token: token }, flocker);
        if (r.data && r.data.success && r.data.data) {
          this.bill = r.data.data.bill || {};
          this.lines = r.data.data.lines || [];
          this.counters = r.data.data.counters || [];
        }
      } catch(e){}
      this.loading = false;
    },
    goToLogin(){
      sessionStorage.setItem('flockr_post_login_redirect', '/bills/' + this.$route.params.token);
      this.$router.push('/login');
    },
    async acceptAndPay(){
      if (!this.payPhone || this.payPhone.length < 9) { alert('Enter a valid M-Pesa phone number'); return; }
      this.submitting = true;
      try {
        var r = await executeJson({
          function: 'acceptBill',
          billId: this.bill.id,
          buyerId: sessionStorage.getItem('userId') || '',
          phone: this.payPhone
        }, flocker);
        if (r.data && r.data.success && r.data.data) {
          this.stkStatus = 'stk_sent';
          this.stkRef = r.data.data.paymentRef || '';
          this.stkCheckoutId = r.data.data.checkoutRequestId || '';
          this.startPolling();
        } else {
          alert((r.data && r.data.message) || 'Could not initiate payment');
        }
      } catch(e){ alert('Network error'); }
      this.submitting = false;
    },
    startPolling(){
      this.stopPolling();
      this.stkCountdown = 5;
      var self = this;
      this.stkTimer = setInterval(function(){
        self.stkCountdown--;
        if (self.stkCountdown <= 0) { self.stkCountdown = 5; self.checkStatus(); }
      }, 1000);
    },
    stopPolling(){ if (this.stkTimer) { clearInterval(this.stkTimer); this.stkTimer = null; } },
    async checkStatus(){
      if (!this.stkRef && !this.stkCheckoutId) return;
      try {
        var f = new FormData(); f.append('function','checkPaymentStatus');
        if (this.stkRef) f.append('paymentRef', this.stkRef);
        if (this.stkCheckoutId) f.append('checkoutRequestId', this.stkCheckoutId);
        var r = await execute(f, flocker);
        if (r.data && r.data.data) {
          var s = r.data.data.status;
          if (s === 'completed' || s === 'success') { this.stkStatus = 'completed'; this.stopPolling(); this.load(); }
          else if (s === 'failed' || s === 'cancelled' || s === 'timeout') { this.stkStatus = 'failed'; this.stkError = r.data.data.message || s; this.stopPolling(); }
        }
      } catch(e){}
    },
    async reject(){
      if (!confirm('Reject this bill? The seller will be notified.')) return;
      try {
        var r = await executeJson({ function: 'rejectBill', billId: this.bill.id, buyerId: sessionStorage.getItem('userId') || '' }, flocker);
        if (r.data && r.data.success) { alert('Bill rejected.'); this.load(); }
      } catch(e){}
    },
    async sendCounter(){
      if (!this.counterAmount || this.counterAmount <= 0) return;
      try {
        var r = await executeJson({
          function: 'counterBill',
          billId: this.bill.id,
          buyerId: sessionStorage.getItem('userId') || '',
          proposedTotal: this.counterAmount,
          message: this.counterMessage
        }, flocker);
        if (r.data && r.data.success) {
          alert('Counter-offer sent. The seller will decide.');
          this.showCounter = false;
          this.load();
        }
      } catch(e){}
    },
    printPdf(){ window.print(); }
  }
};
</script>

<style scoped>
.bv-card { background:#FFFFFF; border:1px solid #E8E2DA; border-radius:12px; padding:24px; margin-bottom:14px; color:#1A1A1A; }
html[data-theme="dark"] .bv-card { background:#141414; border-color:#1E1E1E; color:#F5F0E8; }
.bv-status { display:inline-block; padding:3px 12px; border-radius:12px; font-size:10px; font-weight:800; text-transform:uppercase; letter-spacing:.5px; }
.bv-status-sent { background:rgba(255,171,0,.15); color:#FFAB00; }
.bv-status-accepted { background:rgba(79,195,247,.15); color:#4FC3F7; }
.bv-status-paid { background:rgba(80,200,120,.15); color:#50C878; }
.bv-status-countered { background:rgba(206,147,216,.15); color:#CE93D8; }
.bv-status-rejected { background:rgba(255,107,107,.15); color:#FF6B6B; }
.bv-status-cancelled { background:rgba(107,99,86,.15); color:#6B6356; }
.bv-status-expired { background:rgba(107,99,86,.15); color:#6B6356; }
.bv-party-block { border-top:1px solid rgba(191,161,100,.15); padding:12px 0; }
.bv-party-label { font-size:10px; color:#6B6356; font-weight:600; text-transform:uppercase; letter-spacing:.5px; margin-bottom:6px; }
.bv-avatar { width:44px; height:44px; border-radius:50%; background:#BFA164; color:#fff; display:flex; align-items:center; justify-content:center; font-weight:700; overflow:hidden; }
.bv-avatar img { width:100%; height:100%; object-fit:cover; }
.bv-section { border-top:1px solid rgba(191,161,100,.15); padding-top:12px; margin-top:12px; }
.bv-section-title { font-size:11px; color:#BFA164; font-weight:800; text-transform:uppercase; letter-spacing:.6px; margin-bottom:8px; }
.bv-table { width:100%; font-size:13px; }
.bv-table th { font-size:10px; color:#6B6356; text-transform:uppercase; padding:6px 4px; border-bottom:1px solid #E8E2DA; text-align:left; }
.bv-table td { padding:8px 4px; border-bottom:1px dashed rgba(191,161,100,.15); }
.bv-totals { padding-top:14px; }
.bv-total-line { display:flex; justify-content:space-between; padding:4px 0; font-size:13px; }
.bv-total-grand { border-top:1px solid #BFA164; font-weight:800; font-size:18px; padding-top:12px; margin-top:8px; color:#BFA164; }
.bv-notes { background:rgba(191,161,100,.06); border-radius:8px; padding:10px 12px; margin-top:12px; font-size:12px; }
.bv-escrow-badge { background:rgba(80,200,120,.10); color:#50C878; border-radius:8px; padding:10px 12px; margin-top:12px; font-size:12px; text-align:center; font-weight:600; }
.bv-input { width:100%; padding:9px 12px; border:1px solid #E0D8D0; border-radius:8px; font-size:13px; color:#1A1A1A; background:#FFFFFF; }
html[data-theme="dark"] .bv-input { background:#0F0F0F; border-color:#2A2520; color:#F5F0E8; }
.bv-label { display:block; font-size:11px; color:#6B6356; font-weight:600; text-transform:uppercase; letter-spacing:.4px; margin-bottom:4px; }
@media print {
  .no-print { display:none !important; }
  .bv-card { box-shadow:none; border-color:#333; }
}
</style>
